import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';

export interface Ministerio {
  id: string;
  nombre: string;
  descripcion?: string;
  mision?: string;
  vision?: string;
  estado: boolean;
  creadoEn: string;
  actualizadoEn: string;
  lider?: { id: string; nombres: string; apellidos: string; email: string; telefono: string } | null;
  integrantes?: Array<{
    id: string;
    membresiaId: string;
    rol?: string;
    fechaUnion: string;
    membresia: { id: string; nombres: string; apellidos: string; email: string };
  }>;
  eventos?: Array<{
    id: string;
    titulo: string;
    fecha: string;
    horaInicio?: string;
    horaFin?: string;
    lugar?: string;
    estado: string;
  }>;
}

export interface Evento {
  id: string;
  titulo: string;
  descripcion?: string;
  fecha: string;
  horaInicio?: string;
  horaFin?: string;
  lugar?: string;
  estado: string;
  motivoEstado?: string;
  costoPorPersona?: number;
  presupuesto?: number;
  notasPresupuesto?: string;
  requiereInscripcion: boolean;
  capacidadMaxima?: number;
  esPublico?: boolean;
  creadoEn: string;
  actualizadoEn: string;
  ministerioIds: string[];
  ministerios?: Array<{ id: string; nombre: string; descripcion?: string; estado: boolean }>;
  creadorId: string;
  creador?: { id: string; nombreCompleto: string; email: string };
  asistencias?: Array<{
    id: string;
    membresiaId: string;
    asistio: boolean;
    membresia: { id: string; nombres: string; apellidos: string };
  }>;
  inscripciones?: Array<{
    id: string;
    membresiaId: string;
    estado: string;
    fechaInscripcion: string;
    membresia: { id: string; nombres: string; apellidos: string; email: string };
  }>;
  movimientos?: EventoMovimiento[];
  totalIngresos?: number;
  totalGastos?: number;
  balanceEjecutado?: number;
}

export interface EventoMovimiento {
  id: string;
  eventoId: string;
  tipo: 'ingreso' | 'gasto';
  monto: number;
  descripcion?: string | null;
  fecha: string;
  creadorId?: string | null;
  creador?: { id: string; nombreCompleto: string } | null;
}

export interface CreateMovimientoDto {
  tipo: 'ingreso' | 'gasto';
  monto: number;
  descripcion?: string;
  fecha?: string;
  creadorId?: string;
}

export interface ReporteAsistenciaMiembro {
  membresiaId: string;
  nombres: string;
  apellidos: string;
  rol: string | null;
  asistencias: number;
  inasistencias: number;
  sinRegistro: number;
  porcentaje: number;
}

export interface ReporteAsistenciaEvento {
  eventoId: string;
  titulo: string;
  fecha: string;
  asistencias: number;
}

export interface ReporteAsistencia {
  totalEventos: number;
  desde?: string;
  hasta?: string;
  miembros: ReporteAsistenciaMiembro[];
  eventos: ReporteAsistenciaEvento[];
}

export interface InscripcionEvento {
  id: string;
  eventoId: string;
  membresiaId: string;
  fechaInscripcion: string;
  estado: string;
  notas?: string;
  membresia?: { id: string; nombres: string; apellidos: string; email: string };
}

export interface CreateMinisterioDto {
  nombre: string;
  descripcion?: string;
  mision?: string;
  vision?: string;
  liderId?: string;
}

export interface CreateEventoDto {
  titulo: string;
  descripcion?: string;
  fecha: string;
  horaInicio?: string;
  horaFin?: string;
  lugar?: string;
  estado?: string;
  motivoEstado?: string;
  costoPorPersona?: number;
  presupuesto?: number;
  notasPresupuesto?: string;
  requiereInscripcion?: boolean;
  capacidadMaxima?: number;
  esPublico?: boolean;
  ministerioIds: string[];
  creadorId: string;
}

export interface AddIntegranteDto {
  membresiaId: string;
  rol?: string;
}

class MinistriesServiceClass {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async findAll(page = 1, limit = 10, search = '', mine = false): Promise<{ data: Ministerio[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (search) params.set('search', search);
    const url = mine ? API_ENDPOINTS.ministries.mine : API_ENDPOINTS.ministries.list;
    const response = await fetch(`${API_BASE_URL}${url}?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al listar ministerios');
    }
    const result = await response.json();
    return result.data || result;
  }

  async getById(id: string): Promise<Ministerio> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.detail(id)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al obtener el ministerio');
    }
    const result = await response.json();
    return result.data || result;
  }

  async create(data: CreateMinisterioDto): Promise<Ministerio> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.create}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al crear el ministerio');
    }
    const result = await response.json();
    return result.data || result;
  }

  async update(id: string, data: Partial<CreateMinisterioDto>): Promise<Ministerio> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.update(id)}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al actualizar el ministerio');
    }
    const result = await response.json();
    return result.data || result;
  }

  async toggleStatus(id: string): Promise<Ministerio> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.toggleStatus(id)}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al cambiar el estado del ministerio');
    }
    const result = await response.json();
    return result.data || result;
  }

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.delete(id)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al eliminar el ministerio');
    }
  }

  async addIntegrante(id: string, data: AddIntegranteDto): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.integrantes(id)}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al agregar integrante');
    }
  }

  async removeIntegrante(id: string, membresiaId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.integrantes(id)}/${membresiaId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al remover integrante');
    }
  }

  async findAllEventosPublic(): Promise<Evento[]> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.publicEventos}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al listar eventos públicos');
    }
    const result = await response.json();
    return result.data || result;
  }

  async findAllEventos(): Promise<Evento[]> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.allEventos}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al listar eventos');
    }
    const result = await response.json();
    return result.data || result;
  }

  async listEventos(id: string): Promise<Evento[]> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.eventos(id)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al listar eventos');
    }
    const result = await response.json();
    return result.data || result;
  }

  async createEvento(id: string, data: CreateEventoDto): Promise<Evento> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.eventos(id)}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al crear el evento');
    }
    const result = await response.json();
    return result.data || result;
  }

  async getEvento(id: string): Promise<Evento> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.eventoDetail(id)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al obtener el evento');
    }
    const result = await response.json();
    return result.data || result;
  }

  async updateEvento(id: string, data: Partial<CreateEventoDto>): Promise<Evento> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.eventoUpdate(id)}`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al actualizar el evento');
    }
    const result = await response.json();
    return result.data || result;
  }

  async deleteEvento(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.eventoDelete(id)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al eliminar el evento');
    }
  }

  async getAsistencia(eventoId: string): Promise<Evento['asistencias']> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.asistencia(eventoId)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al obtener asistencias');
    }
    const result = await response.json();
    return result.data || result;
  }

  async registerAsistencia(eventoId: string, membresiaIds: string[]): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.asistencia(eventoId)}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ membresiaIds }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al registrar asistencia');
    }
  }

  // Inscripciones
  async getInscripciones(eventoId: string): Promise<InscripcionEvento[]> {
    const response = await fetch(`${API_BASE_URL}/eventos/${eventoId}/inscripciones`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al obtener inscripciones');
    }
    const result = await response.json();
    return result.data || result;
  }

  async inscribirMiembro(eventoId: string, membresiaId: string, notas?: string): Promise<InscripcionEvento> {
    const response = await fetch(`${API_BASE_URL}/eventos/${eventoId}/inscripciones`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ membresiaId, notas }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al inscribir miembro');
    }
    const result = await response.json();
    return result.data || result;
  }

  async cancelarInscripcion(eventoId: string, inscripcionId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/eventos/${eventoId}/inscripciones/${inscripcionId}/cancelar`, {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al cancelar inscripción');
    }
  }

  // Movimientos financieros
  async getMovimientos(eventoId: string): Promise<EventoMovimiento[]> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.movimientos(eventoId)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al obtener movimientos');
    }
    const result = await response.json();
    return result.data || result;
  }

  async addMovimiento(eventoId: string, data: CreateMovimientoDto): Promise<EventoMovimiento> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.movimientos(eventoId)}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al registrar movimiento');
    }
    const result = await response.json();
    return result.data || result;
  }

  async deleteMovimiento(movimientoId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.deleteMovimiento(movimientoId)}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al eliminar movimiento');
    }
  }

  // Reporte de asistencia
  async getReporteAsistencia(ministerioId: string, desde?: string, hasta?: string): Promise<ReporteAsistencia> {
    const params = new URLSearchParams();
    if (desde) params.set('desde', desde);
    if (hasta) params.set('hasta', hasta);
    const query = params.toString();
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ministries.reporteAsistencia(ministerioId)}${query ? `?${query}` : ''}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
      throw new Error(errorData.message || 'Error al obtener reporte de asistencia');
    }
    const result = await response.json();
    return result.data || result;
  }
}

export const MinistriesService = new MinistriesServiceClass();
